import React from "react";

// Basic placeholder terms. Update with help of legal counsel before relying on
// it for enforceability, dispute resolution, or limitation-of-liability claims.
const LAST_UPDATED = "May 17, 2026";

function TermsOfService() {
  return (
    <div className="settings-section terms-of-service">
      <div className="section-header">
        <h2>Terms of Service</h2>
        <p className="section-description">Last updated: {LAST_UPDATED}</p>
      </div>

      <div className="card" style={{ maxWidth: 820 }}>
        <div className="card-content" style={{ lineHeight: 1.65 }}>
        <p>
          These Terms of Service ("Terms") govern your access to and use of the
          Chariot platform at chariot.poseidonmarine.com (the "Service"),
          operated by Poseidon Marine and its affiliated entities (collectively
          "Poseidon Marine", "we", "us", "our"):
        </p>
        <ul>
          <li>Poseidon Marine National LLC</li>
          <li>PM Mechanics Cape Coral LLC</li>
          <li>PM Mechanics Destin LLC</li>
          <li>PM Mechanics Tampa LLC</li>
          <li>PM Mechanics Orlando LLC</li>
          <li>PM Mechanics Miami LLC</li>
          <li>PM Mechanics Jacksonville LLC</li>
        </ul>
        <p>
          By accessing or using the Service you agree to these Terms. If you do
          not agree, do not use the Service.
        </p>

        <h3>1. Accounts</h3>
        <ul>
          <li>
            You are responsible for the accuracy of the information you provide
            and for maintaining the security of your account credentials.
          </li>
          <li>
            You will not share your password and will notify us immediately at{" "}
            <a href="mailto:support@poseidonmarine.com">support@poseidonmarine.com</a>{" "}
            if you suspect unauthorized access.
          </li>
          <li>
            You must be at least 18 years old (or the age of majority in your
            jurisdiction) to create an account.
          </li>
        </ul>

        <h3>2. Acceptable Use</h3>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Service to violate any law or third-party right.</li>
          <li>
            Attempt to gain unauthorized access to the Service, other accounts,
            or our infrastructure; probe, scan, or test for vulnerabilities
            without our written authorization.
          </li>
          <li>
            Upload malware, transmit spam, or send messages that violate the
            CAN-SPAM Act, TCPA, or similar laws.
          </li>
          <li>
            Reverse engineer, decompile, or attempt to extract source code from
            the Service except as permitted by law.
          </li>
          <li>
            Use the Service to harass, threaten, defame, or harm any person or
            collect data about others without their consent.
          </li>
          <li>
            Resell, sublicense, or otherwise commercialize access to the
            Service without our prior written consent.
          </li>
        </ul>

        <h3>3. Customer Data and Content</h3>
        <p>
          You retain ownership of the data you submit to the Service ("Customer
          Data"). You grant us a limited license to host, process, transmit,
          and display Customer Data solely as needed to provide and improve the
          Service. You are responsible for ensuring you have the rights and
          consents necessary to upload Customer Data, including any personal
          information about your customers.
        </p>

        <h3>4. Payments and Subscriptions</h3>
        <ul>
          <li>
            Paid features are billed in advance on a recurring basis (monthly
            or annual, as selected) until cancelled.
          </li>
          <li>
            Payments are processed by one or more PCI-compliant payment
            processors that we use under contract (currently Stripe, Square,
            Intuit QuickBooks Payments, Authorize.net, and Chase Paymentech /
            JPMorgan Chase Merchant Services, and may include others we add or
            switch to over time). By submitting payment information you also
            agree to the terms of the processor handling that transaction.
          </li>
          <li>
            Fees are non-refundable except where required by law or where we
            expressly agree otherwise.
          </li>
          <li>
            We may change pricing on prospective billing periods with at least
            30 days' notice.
          </li>
          <li>
            If a payment fails, we may suspend access to paid features until
            the balance is paid.
          </li>
          <li>
            <strong>Disputes and chargebacks.</strong> If you believe a charge
            is incorrect, please contact us at{" "}
            <a href="mailto:billing@poseidonmarine.com">billing@poseidonmarine.com</a>{" "}
            before initiating a chargeback so we can review and, if
            appropriate, refund the charge directly. You retain the right to
            dispute charges with your card issuer at any time.
          </li>
        </ul>

        <h3>5. Termination</h3>
        <p>
          You may stop using the Service at any time. We may suspend or
          terminate your access if you violate these Terms, fail to pay, or if
          we are required to do so by law. On termination we may delete or
          retain your data per our{" "}
          <a href="/privacy">Privacy Policy</a> and applicable retention
          requirements.
        </p>

        <h3>6. Third-Party Services</h3>
        <p>
          The Service integrates with third-party providers, including payment
          processors (Stripe, Square, Intuit QuickBooks Payments,
          Authorize.net, and Chase Paymentech / JPMorgan Chase Merchant
          Services), a PCI Level 1 card vault (PCI Vault), email (Brevo), SMS
          (Twilio), phone (RingCentral), hosting (Google Cloud), and AI
          features (Anthropic). Your use of those providers is also subject to
          their respective terms and privacy policies. We are not responsible
          for third-party services we do not control.
        </p>

        <h3>7. Service Availability</h3>
        <p>
          We aim to keep the Service available but do not guarantee
          uninterrupted, error-free, or perfectly secure operation. We may
          modify, suspend, or discontinue any part of the Service at any time
          with reasonable notice when practical.
        </p>

        <h3>8. Intellectual Property</h3>
        <p>
          The Service, including its software, design, and content (other than
          Customer Data), is owned by Poseidon Marine or our licensors and is
          protected by copyright, trademark, and other laws. We grant you a
          limited, non-exclusive, non-transferable license to use the Service
          for its intended purpose during the term of your account.
        </p>

        <h3>9. Disclaimers</h3>
        <p style={{ textTransform: "uppercase", fontSize: 13 }}>
          The service is provided "as is" and "as available" without warranties
          of any kind, whether express or implied, including without limitation
          warranties of merchantability, fitness for a particular purpose,
          non-infringement, or that the service will be uninterrupted, secure,
          or error-free.
        </p>

        <h3>10. Limitation of Liability</h3>
        <p style={{ textTransform: "uppercase", fontSize: 13 }}>
          To the fullest extent permitted by law, Poseidon Marine and its
          officers, employees, and contractors will not be liable for indirect,
          incidental, special, consequential, or punitive damages, or for lost
          profits, revenues, data, or goodwill, arising out of or related to
          the service. Our total aggregate liability for any claim arising out
          of or related to the service will not exceed the amount you paid us
          in the twelve months preceding the event giving rise to the claim, or
          one hundred dollars (US$100), whichever is greater.
        </p>

        <h3>11. Indemnification</h3>
        <p>
          You agree to indemnify and hold harmless Poseidon Marine and its
          personnel from any claim, loss, or expense (including reasonable
          attorneys' fees) arising out of your use of the Service, your
          Customer Data, or your violation of these Terms.
        </p>

        <h3>12. Governing Law and Disputes</h3>
        <p>
          These Terms are governed by the laws of the State of Florida, without
          regard to conflict-of-laws principles. Any dispute will be resolved
          exclusively in the state or federal courts located in Hillsborough
          County, Florida, and you consent to the personal jurisdiction of
          those courts.
        </p>

        <h3>13. Changes to These Terms</h3>
        <p>
          We may update these Terms from time to time. The "Last updated" date
          at the top reflects the most recent change. Material changes will be
          communicated through the Service or by email. Continued use after a
          change means you accept the updated Terms.
        </p>

        <h3>14. Privacy and SMS Communications</h3>
        <p>
          Your privacy is very important to us. We collect and use your
          information solely to provide and improve our services. We do not
          share, sell, or lease your personal data with any third parties. For
          more detailed information on how your data is managed, please review
          our <a href="/privacy">Privacy Policy</a>.
        </p>
        <p>
          <strong>SMS Messaging Program:</strong> When you provide your mobile
          phone number through our website or customer portal, you may opt in
          to receive SMS text messages from Poseidon Marine. These messages may
          include appointment confirmations, job status updates, invoice
          notifications, and service-related communications.
        </p>
        <ul>
          <li><strong>Program Name:</strong> Poseidon Marine Messaging</li>
          <li><strong>Message Frequency:</strong> Varies based on your service activity</li>
          <li>Message and data rates may apply</li>
          <li>
            To opt out at any time, reply <strong>STOP</strong> to any message.
            You will receive a single confirmation and no further messages will
            be sent.
          </li>
          <li>
            To request help, reply <strong>HELP</strong> or contact us at{" "}
            <a href="mailto:support@poseidonmarine.com">support@poseidonmarine.com</a>.
          </li>
          <li>
            Consent to receive SMS messages is not required as a condition of
            any purchase or service.
          </li>
          <li>Mobile opt-in information is never shared with third parties.</li>
        </ul>

        <h3>15. Contact</h3>
        <p>
          Questions about these Terms can be sent to{" "}
          <a href="mailto:legal@poseidonmarine.com">legal@poseidonmarine.com</a>.
        </p>

        <p style={{ marginTop: 24, fontSize: 13, color: "var(--text-tertiary)" }}>
          This is a starter agreement provided for transparency. It is not
          legal advice. Consult an attorney before relying on it for
          enforceability, dispute resolution, indemnification, limitation of
          liability, or specific regulations applicable to your business.
        </p>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;
